/**
 * Jobs Service - API calls for centralized job tracking
 */

import { apiClient } from "./apiClient";

export type JobStatus =
  | "queued"
  | "running"
  | "completed"
  | "failed"
  | "cancelled"
  | "invalidated";

export type JobType =
  | "image_processing"
  | "video_processing"
  | "image_analysis"
  | "post_preparation"
  | "social_publish"
  | "social_sync"
  | "media_download"
  | "media_share"
  | "caption_generation"
  | "text_generation";

export type TextGenerationType =
  | "caption"
  | "brief_distillation"
  | "idea_generation";

export type EntityType =
  | "media_item"
  | "post"
  | "media_share"
  | "batch"
  | "scheduled_post"
  | "client";

export interface JobSummary {
  id: string;
  jobType: JobType;
  entityType: EntityType;
  entityId: string;
  status: JobStatus;
  progress: number;
  progressMessage: string | null;
  createdAt: string;
  errorCode: string | null;
}

export interface JobDetails extends JobSummary {
  clientId: string;
  inputData: Record<string, unknown> | null;
  executionId: string | null;
  startedAt: string | null;
  completedAt: string | null;
  attemptCount: number;
  maxAttempts: number;
  parentJobId: string | null;
  errorMessage: string | null;
  resultData: Record<string, unknown> | null;
  invalidatedAt: string | null;
  invalidationReason: string | null;
}

interface ListJobsResponse {
  jobs: JobSummary[];
  count: number;
  hasMore: boolean;
}

interface ListJobsOptions {
  status?: JobStatus;
  job_type?: JobType;
  limit?: number;
  offset?: number;
}

/**
 * Get all jobs for a client with optional filters
 */
export async function getClientJobs(
  clientId: string,
  options: ListJobsOptions = {}
): Promise<ListJobsResponse> {
  const params = new URLSearchParams();
  if (options.status) params.append("status", options.status);
  if (options.job_type) params.append("job_type", options.job_type);
  if (options.limit) params.append("limit", options.limit.toString());
  if (options.offset) params.append("offset", options.offset.toString());

  const queryString = params.toString();
  const url = `/${clientId}/jobs${queryString ? `?${queryString}` : ""}`;

  return apiClient.get<ListJobsResponse>(url);
}

/**
 * Get all currently running/queued jobs for a client
 */
export async function getRunningJobs(
  clientId: string
): Promise<{ jobs: JobSummary[]; count: number }> {
  return apiClient.get<{ jobs: JobSummary[]; count: number }>(
    `/${clientId}/jobs/running`
  );
}

/**
 * Get a single job by ID
 */
export async function getJob(
  clientId: string,
  jobId: string
): Promise<JobDetails> {
  return apiClient.get<JobDetails>(`/${clientId}/jobs/${jobId}`);
}

/**
 * Get the latest job for a specific entity
 */
export async function getJobForEntity(
  clientId: string,
  entityType: EntityType,
  entityId: string
): Promise<JobDetails> {
  return apiClient.get<JobDetails>(
    `/${clientId}/jobs/entity/${entityType}/${entityId}`
  );
}

/**
 * Cancel a running or queued job
 */
export async function cancelJob(
  clientId: string,
  jobId: string,
  reason?: string
): Promise<{ id: string; status: JobStatus; message: string }> {
  return apiClient.post<{ id: string; status: JobStatus; message: string }>(
    `/${clientId}/jobs/${jobId}/cancel`,
    reason ? { reason } : {}
  );
}

/**
 * Check if a job is in a terminal state
 */
export function isTerminalStatus(status: JobStatus): boolean {
  return ["completed", "failed", "cancelled", "invalidated"].includes(status);
}

/**
 * Check if a job is in progress
 */
export function isActiveStatus(status: JobStatus): boolean {
  return ["queued", "running"].includes(status);
}
